import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  addDoc,
  updateDoc,
  deleteDoc,
  runTransaction,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Friend system:
 * - friendRequests: { fromUid, fromUsername, toUid, toUsername, status, createdAt }
 * - friends: { uidA, uidB, createdAt } with id `${minUid}_${maxUid}`
 */
export function useFriends(currentUser) {
  const uid = currentUser?.uid;
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Build pair id deterministically
  const pairId = (a, b) => (a < b ? `${a}_${b}` : `${b}_${a}`);

  useEffect(() => {
    if (!uid) {
      setIncomingRequests([]);
      setFriends([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    // Listen to incoming pending requests
    const reqQ = query(
      collection(db, 'friendRequests'),
      where('toUid', '==', uid),
      where('status', '==', 'pending')
    );
    const unsubReq = onSnapshot(reqQ, async (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Fetch profile images for incoming requests
      const withProfiles = await Promise.all(list.map(async (req) => {
        try {
          const profileSnap = await getDoc(doc(db, 'profiles', req.fromUid));
          const profileData = profileSnap.exists() ? profileSnap.data() : {};
          return { ...req, profileImage: profileData.profileImage || null };
        } catch (err) {
          console.error('Failed to load profile for request', err);
          return req;
        }
      }));
      setIncomingRequests(withProfiles);
    }, (err) => setError(err.message || 'Failed to load requests'));

    // Listen to friends (two queries, merge)
    const friendsQ1 = query(collection(db, 'friends'), where('uidA', '==', uid));
    const friendsQ2 = query(collection(db, 'friends'), where('uidB', '==', uid));

    const unsub1 = onSnapshot(friendsQ1, async (snap) => {
      const friendUids = snap.docs.map((d) => d.data().uidB);
      const profiles = await Promise.all(friendUids.map((fid) => getDoc(doc(db, 'profiles', fid))));
      const hydrated = profiles
        .map((p, idx) => {
          if (!p.exists()) {
            return null; // Filter out deleted accounts
          }
          return { uid: friendUids[idx], ...p.data() };
        })
        .filter(Boolean);
      setFriends((prev) => {
        const other = prev.filter((f) => !friendUids.includes(f.uid));
        return [...other, ...hydrated];
      });
    }, (err) => setError(err.message || 'Failed to load friends'));

    const unsub2 = onSnapshot(friendsQ2, async (snap) => {
      const friendUids = snap.docs.map((d) => d.data().uidA);
      const profiles = await Promise.all(friendUids.map((fid) => getDoc(doc(db, 'profiles', fid))));
      const hydrated = profiles
        .map((p, idx) => {
          if (!p.exists()) {
            return null; // Filter out deleted accounts
          }
          return { uid: friendUids[idx], ...p.data() };
        })
        .filter(Boolean);
      setFriends((prev) => {
        const other = prev.filter((f) => !friendUids.includes(f.uid));
        return [...other, ...hydrated];
      });
    }, (err) => setError(err.message || 'Failed to load friends'));

    setLoading(false);
    return () => {
      unsubReq();
      unsub1();
      unsub2();
    };
  }, [uid]);

  const sendRequest = async (toUsernameRaw) => {
    if (!uid) throw new Error('Not signed in');
    const toUsername = toUsernameRaw.trim();
    if (!toUsername) throw new Error('Enter a username');
    const toLower = toUsername.toLowerCase();

    setError(null);
    // Lookup recipient uid by username registry
    const usernameSnap = await getDoc(doc(db, 'usernames', toLower));
    if (!usernameSnap.exists()) throw new Error('User not found');
    const toUid = usernameSnap.data().uid;
    if (toUid === uid) throw new Error('You cannot add yourself');

    // Already friends?
    const pId = pairId(uid, toUid);
    const friendDoc = await getDoc(doc(db, 'friends', pId));
    if (friendDoc.exists()) throw new Error('Already friends');

    // Existing pending request?
    const outgoingQ = query(collection(db, 'friendRequests'), where('fromUid', '==', uid), where('toUid', '==', toUid), where('status', '==', 'pending'));
    const incomingQ = query(collection(db, 'friendRequests'), where('fromUid', '==', toUid), where('toUid', '==', uid), where('status', '==', 'pending'));
    const [outgoingSnap, incomingSnap] = await Promise.all([getDocs(outgoingQ), getDocs(incomingQ)]);
    if (!outgoingSnap.empty || !incomingSnap.empty) throw new Error('Pending request already exists');

    // Compose request payload
    const fromUsername = currentUser.username || (currentUser.displayName || '');
    await addDoc(collection(db, 'friendRequests'), {
      fromUid: uid,
      fromUsername,
      toUid,
      toUsername,
      status: 'pending',
      createdAt: serverTimestamp()
    });
  };

  const acceptRequest = async (request) => {
    if (!uid) throw new Error('Not signed in');
    if (request.toUid !== uid) throw new Error('Not your request');
    setError(null);
    const pId = pairId(request.fromUid, request.toUid);
    await runTransaction(db, async (tx) => {
      const reqRef = doc(db, 'friendRequests', request.id);
      const reqSnap = await tx.get(reqRef);
      if (!reqSnap.exists()) throw new Error('Request missing');
      const data = reqSnap.data();
      if (data.status !== 'pending') throw new Error('Request not pending');
      tx.update(reqRef, { status: 'accepted' });
      const friendRef = doc(db, 'friends', pId);
      tx.set(friendRef, { uidA: request.fromUid, uidB: request.toUid, createdAt: serverTimestamp() }, { merge: true });
    });
  };

  const declineRequest = async (request) => {
    if (!uid) throw new Error('Not signed in');
    if (request.toUid !== uid) throw new Error('Not your request');
    setError(null);
    await updateDoc(doc(db, 'friendRequests', request.id), { status: 'declined' });
  };

  const findUserByUsername = async (usernameRaw) => {
    const name = usernameRaw.trim().toLowerCase();
    if (!name) throw new Error('Enter a username');
    const snap = await getDoc(doc(db, 'usernames', name));
    if (!snap.exists()) return null;
    const toUid = snap.data().uid;
    const prof = await getDoc(doc(db, 'profiles', toUid));
    if (!prof.exists()) return { uid: toUid, username: usernameRaw };
    return prof.data();
  };

  const removeFriend = async (friendUid) => {
    if (!uid) throw new Error('Not signed in');
    if (!friendUid) throw new Error('Missing friend');
    const pId = pairId(uid, friendUid);
    await deleteDoc(doc(db, 'friends', pId));
  };

  return {
    incomingRequests,
    friends,
    loading,
    error,
    sendRequest,
    acceptRequest,
    declineRequest,
    findUserByUsername,
    removeFriend
  };
}
